import PropTypes from "prop-types"
import React from "react"
import { useFormik } from "formik"
import FileUploadInput from "./FileUploadInput"
import LocationPickerMap from "./LocationPickerMap"
import StoreActivitySelect from "./StoreActivitySelect"
import { storeValidationSchema } from "../partnerSignupValidation"

const FieldError = ({ touched, error }) => touched && error ? <div className="partner-field-error">{error}</div> : null

FieldError.propTypes = {
  error: PropTypes.string,
  touched: PropTypes.bool,
}

const StoreConfirmationForm = ({ initialValues, isManualEntry, onBack, onSubmit, loading, apiError }) => {
  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: storeValidationSchema,
    onSubmit,
  })

  const fieldProps = name => ({
    name,
    value: formik.values[name] ?? "",
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
    className: formik.touched[name] && formik.errors[name] ? "is-invalid" : "",
  })

  return (
    <section className="partner-page partner-store-page">
      <div className="partner-step-heading">
        <div>
          <span className="partner-eyebrow">Votre commerce</span>
          <h1>{isManualEntry ? "Présentez-nous votre commerce" : "Confirmez les informations"}</h1>
          {isManualEntry && (
            <p>Renseignez les informations essentielles et positionnez précisément votre commerce sur la carte.</p>
          )}
        </div>
        <button type="button" className="partner-link-button" onClick={onBack}>
          <i className="bx bx-left-arrow-alt" /> Modifier la recherche
        </button>
      </div>

      {apiError && <div className="partner-alert partner-alert-danger">{apiError}</div>}

      <form onSubmit={formik.handleSubmit} noValidate className="partner-store-layout">
        <div className="partner-form-panel">
          <div className="partner-section-title">
            <span>01</span>
            <div><strong>Identité du commerce</strong><small>Les informations visibles par vos clients.</small></div>
          </div>
          <div className="partner-form-grid">
          <div className="partner-field partner-field-full">
            <label htmlFor="partner-store-name">Nom du commerce *</label>
            <input id="partner-store-name" type="text" placeholder="Nom du commerce" {...fieldProps("name")} />
            <FieldError touched={formik.touched.name} error={formik.errors.name} />
          </div>

          <div className="partner-field partner-field-full">
            <label htmlFor="partner-store-address">Adresse *</label>
            <input id="partner-store-address" type="text" placeholder="Adresse complète" {...fieldProps("address")} />
            <FieldError touched={formik.touched.address} error={formik.errors.address} />
          </div>

          <div className="partner-field">
            <label htmlFor="partner-store-zip">Code postal *</label>
            <input id="partner-store-zip" type="text" inputMode="numeric" placeholder="20000" {...fieldProps("zipCode")} />
            <FieldError touched={formik.touched.zipCode} error={formik.errors.zipCode} />
          </div>

          <div className="partner-field">
            <label htmlFor="partner-store-city">Ville *</label>
            <input id="partner-store-city" type="text" placeholder="Casablanca" {...fieldProps("city")} />
            <FieldError touched={formik.touched.city} error={formik.errors.city} />
          </div>

          <div className="partner-field">
            <label htmlFor="partner-store-country">Pays *</label>
            <input id="partner-store-country" type="text" placeholder="Maroc" {...fieldProps("country")} />
            <FieldError touched={formik.touched.country} error={formik.errors.country} />
          </div>

          <div className="partner-field">
            <label htmlFor="partner-store-website">Site web</label>
            <input id="partner-store-website" type="url" placeholder="https://..." {...fieldProps("website")} />
            <FieldError touched={formik.touched.website} error={formik.errors.website} />
          </div>

          <div className="partner-field">
            <label htmlFor="partner-store-activity">Secteur d’activité *</label>
            <StoreActivitySelect
              name="activityId"
              value={formik.values.activityId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={Boolean(formik.touched.activityId && formik.errors.activityId)}
            />
            <FieldError touched={formik.touched.activityId} error={formik.errors.activityId} />
          </div>

          <fieldset className="partner-field partner-radio-field">
            <legend>Accepte le paiement en espèces</legend>
            <label>
              <input type="radio" name="acceptCash" checked={formik.values.acceptCash === true} onChange={() => formik.setFieldValue("acceptCash", true)} />
              Oui
            </label>
            <label>
              <input type="radio" name="acceptCash" checked={formik.values.acceptCash === false} onChange={() => formik.setFieldValue("acceptCash", false)} />
              Non
            </label>
          </fieldset>

          <div className="partner-field partner-field-full">
            <FileUploadInput
              label="Photo de profil"
              value={formik.values.profilePhoto}
              circular
              onChange={(value, file) => {
                formik.setFieldValue("profilePhoto", value)
                formik.setFieldValue("profilePhotoFile", file)
              }}
            />
          </div>
        </div>
        </div>

        <aside className="partner-map-panel">
          <div className="partner-section-title">
            <span>02</span>
            <div><strong>Position exacte</strong><small>Déplacez le pin pour aider les clients à vous trouver.</small></div>
          </div>
          <LocationPickerMap
            latitude={formik.values.location?.latitude}
            longitude={formik.values.location?.longitude}
            onChange={location => formik.setFieldValue("location", location)}
          />
          {(formik.errors.location?.latitude || formik.errors.location?.longitude) && (
            <div className="partner-field-error partner-location-error">
              Positionnez le pin sur la carte pour définir la localisation du commerce.
            </div>
          )}
        </aside>

        <div className="partner-actions partner-store-actions">
          <button type="button" className="partner-button partner-button-secondary" onClick={onBack}>Retour</button>
          <button type="submit" className="partner-button partner-button-primary" disabled={loading}>
            {loading ? <><span className="partner-spinner" /> Création en cours...</> : <>Créer mon commerce <i className="bx bx-right-arrow-alt" /></>}
          </button>
        </div>
      </form>
    </section>
  )
}

StoreConfirmationForm.propTypes = {
  apiError: PropTypes.string,
  initialValues: PropTypes.object.isRequired,
  isManualEntry: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
}

export default StoreConfirmationForm
